import { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

const ASSET_BASE = process.env.PUBLIC_URL || "";

const careerCards = [
  {
    title: "Where Passion\nMeets Success",
    text: "As a brand that celebrates every individual, we have profound customer love. And as a leader in the retail market, driven by our on-trend products at incredibly low prices, we\u2019re optimistic about our future and yours. We\u2019re relentless in our pursuit for results, but changing at a rapid rate, giving you challenging work that you\u2019ll be proud of.",
    icon: "Passion.png",
    bg: "Blurb1.png",
  },
  {
    title: "Thrive\nTogether",
    text: "We believe good people come from all sorts of places, no matter who you are, what your life looks like or why you joined the team. It\u2019s a collaborative place where leaders are supportive and team members from diverse backgrounds often become friends. We all pitch in to achieve our shared goals and thrive together. We keep things simple and have fun along the way.Like any good family, our culture is supportive, energizing and approachable.",
    icon: "Thfrive.png",
    bg: "Blurb2.png",
  },
  {
    title: "Your Career,\nYour Way",
    text: "Whatever your ambition, there\u2019s opportunity to build your career in any direction or as far as you want to take it. And whatever your way, we celebrate your uniqueness and individuality. We provide an inclusive, flexible environment where you can bring your whole self to work, where everyone feels valued and respected.What matters to you, matters to us.",
    icon: "Career.png",
    bg: "Blurb3.png",
  },
];

const galleryPhotos = [
  { image: `${ASSET_BASE}/assets/gallery001-v2.png`, alt: "Team collaboration" },
  { image: `${ASSET_BASE}/assets/gallery002-v2.png`, alt: "Team meeting" },
  { image: `${ASSET_BASE}/assets/gallery003-v2.png`, alt: "Team celebration" },
  {
    image: `${ASSET_BASE}/assets/gallery004-v2.png`,
    alt: "Team working together",
  },
  { image: `${ASSET_BASE}/assets/gallery005-v2.png`, alt: "Team photo" },
  { image: `${ASSET_BASE}/assets/gallery006-v2.png`, alt: "Team gathering" },
];

function PhotoCard({ image, alt }: { image: string; alt: string }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        height: { xs: 200, sm: 280 },
        minWidth: { xs: 280, sm: 376 },
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={image}
        alt={alt}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 0.5s ease",
          transform: hovered
            ? "translateY(-10px) scale(1.05)"
            : "translateY(0) scale(1)",
        }}
      />
    </Box>
  );
}

function PhotoCarousel() {
  const [scrollPos, setScrollPos] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const allPhotos = [...galleryPhotos, ...galleryPhotos];

  useEffect(() => {
    const interval = setInterval(() => {
      setScrollPos((prev) => {
        const cardWidth = 400;
        const totalWidth = cardWidth * galleryPhotos.length;
        const next = prev + 1;
        return next >= totalWidth ? 0 : next;
      });
    }, 30);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ overflow: "hidden", position: "relative", width: "100%" }}>
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          gap: 3,
          transform: `translateX(-${scrollPos}px)`,
          willChange: "transform",
        }}
      >
        {allPhotos.map((photo, i) => (
          <PhotoCard key={i} image={photo.image} alt={photo.alt} />
        ))}
      </Box>
    </Box>
  );
}

function Careers() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <Box component="main">
      <Box
        component="section"
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "50vh", md: "75vh" },
          maxHeight: { md: "600px", lg: "700px" },
          minHeight: "400px",
          overflow: "hidden",
          mt: { xs: 3.5, md: 7 },
          mb: { xs: 10, md: 10 },
        }}
      >
        <Box
          component="video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        >
          <source
            src={`${ASSET_BASE}/assets/careersvideo.mp4`}
            type="video/mp4"
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: { xs: "24px", md: "64px" },
            transform: "translateY(-50%)",
            color: "common.white",
            maxWidth: { xs: "calc(100% - 48px)", sm: 520 },
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
              fontWeight: 700,
              lineHeight: 1.2,
              animation: "heroFadeUp 0.9s ease both",
              "@keyframes heroFadeUp": {
                from: { opacity: 0, transform: "translateY(36px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            Brighter futures
            <br />
            start here
          </Typography>
        </Box>
      </Box>

      <Box component="section" sx={{ pt: 8, px: 3, textAlign: "center" }}>
        <Typography variant="h2" sx={{ textAlign: "center", m: 0 }}>
          Join the Anko Sourcing Family
        </Typography>
      </Box>

      <Box
        component="section"
        sx={{
          pt: { xs: 8, md: 4 },
          pb: 4,
          px: 3,
          maxWidth: 1400,
          mx: "auto",
          mb: 10,
        }}
      >
        <PhotoCarousel />

        <Box sx={{ maxWidth: 950, mx: "auto", mt: 6 }}>
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              mb: 3,
              textAlign: "left",
              fontSize: { xs: "1.2rem", md: "1.125rem", lg: "1.2rem" },
              lineHeight: 1.2,
              letterSpacing: "0.01em",
            }}
          >
            You can expect a dynamic work environment that is highly
            collaborative, values-driven and fast-paced where you will be
            challenged to achieve business goals with no two days being the
            same! You will be encouraged to exhibit curiosity and avail
            opportunities for continuous learning.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              textAlign: "left",
              fontSize: { xs: "1.2rem", md: "1.125rem", lg: "1.2rem" },
              lineHeight: 1.2,
              letterSpacing: "0.01em",
            }}
          >
            Our learning interventions are focused on supporting the achievement
            of your personal goals along with the business goals. A
            high-performing team that strives to consistently raise the bar,
            aided by an inclusive and progressive environment that enables you
            to reach beyond your potential.
          </Typography>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          position: "relative",
          width: "100%",
          // minHeight: { xs: 450, md: 400 },

          backgroundImage: {
            xs: `url(${ASSET_BASE}/assets/mobileview.png)`,
            md: `url(${ASSET_BASE}/assets/testBanner11-v2.png)`,
          },
          backgroundSize: "cover",
          backgroundPositionX: "right 0px",
          backgroundPositionY: "center",
          backgroundRepeat: "no-repeat",
          bgcolor: "#63a908",
          paddingY: { xs: 6, md: 15 },
        }}
      >
        <Box
          sx={{
            width: { xs: "90%", md: "50%" },
            mb: { xs: 8, md: 0 },
            paddingLeft: { xs: 3, md: 12 },
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "common.white",
              mb: 3,
              fontSize: {
                xs: "1.5rem",
                sm: "1.9rem",
                md: "2.4rem",
                lg: "2.4rem",
              },
              fontWeight: 700,
              whiteSpace: "pre-wrap",
              // overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Let's build your future together.
          </Typography>
          <Button
            variant="outlined"
            component="a"
            href="https://jobs.kmart.com.au/KGA/?locale=en_GB"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderRadius: "16px 16px 0px 16px",
              border: "1.2px solid #ffffff",
              color: "common.white",
              fontSize: { xs: "1rem", md: "0.95rem", lg: "1rem" },
              textTransform: "none",
              "&:hover": {
                border: "1.5px solid #ffffff",
                bgcolor: "common.white",
                color: "#b7127e",
              },
            }}
          >
            View Jobs
          </Button>
        </Box>
      </Box>

      <Box
        component="section"
        sx={{
          pt: { xs: 2, md: 5 },
          pb: { xs: 2, md: 4 },
          px: 3,
          maxWidth: 1400,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 4, md: 8 },
            py: { xs: 3, md: 6 },
            px: { xs: 3, md: 8 },
          }}
        >
          {/* IMAGE BOX */}
          <Box
            component="img"
            src={`${ASSET_BASE}/assets/S-v2.png`}
            alt="Anko Sourcing Team"
            sx={{
              width: { xs: "100%", md: "45%" },
              maxWidth: 600,
              borderRadius: "24px",
              objectFit: "cover",
              height: "auto",
            }}
          />

          {/* TEXT BOX */}
          <Box
            sx={{
              flex: 1,
              maxWidth: { xs: "100%", md: 550 },
              textAlign: "left",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem" },
                color: "#333",
                mb: 3,
                lineHeight: 1.5,
              }}
            >
              At Anko Sourcing, we believe our strength lies in our people.
              You’ll find an energetic workplace where collaboration thrives,
              and creativity is celebrated.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem" },
                color: "#333",
                lineHeight: 1.5,
              }}
            >
              Our non-hierarchical work culture embraces diversity, encouraging
              every team member to bring their unique perspective and
              experiences to the table.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* FINAL SECTION - THE CARDS */}
      <Box
        component="section"
        sx={{
          mt: { xs: -2, md: 0 },
          pt: { xs: 2, md: 4 },
          pb: 10,
          px: { xs: 3, md: 6, lg: 8 },
          width: "100%",
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 4, md: 2, lg: 4, xl: 6 },
            alignItems: { xs: "center", md: "stretch" },
            maxWidth: 1600,
            mx: "auto",
          }}
        >
          {careerCards.map((card) => (
            <Box
              key={card.title}
              sx={{
                position: "relative",
                borderRadius: "20px",
                color: "common.white",
                display: "flex",
                flexDirection: "column",
                flex: { xs: "0 1 320px", md: "1 1 0" },
                overflow: "hidden",
                /* Height Adjustment */
                /* --- CARD HEIGHT REDUCTION --- */
                /* Lowering the minHeight so the cards don't look stretched */
                minHeight: {
                  xs: 520,
                  md: 500, // Reduced from 540 for Windows
                  lg: 600, // Kept for Mac
                },
                // Override for the specific Windows/Laptop range
                "@media (min-width:1024px) and (max-width:1550px)": {
                  minHeight: "510px !important",
                },
              }}
            >
              <Box
                component="img"
                src={`${ASSET_BASE}/assets/${card.bg}`}
                alt=""
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  /* Pins design to bottom for Mac, centers for Windows scaling safety */
                  objectPosition: "bottom center",
                  "@media (min-width:1200px) and (max-width:1500px)": {
                    objectPosition: "center",
                  },
                  zIndex: 0,
                }}
              />

              {/* Content Overlay */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  /* Responsive Padding: Reduced for Windows/Laptop */
                  p: {
                    xs: "32px 20px",
                    md: "24px 16px",
                    lg: "44px 32px",
                  },
                  "@media (min-width:1200px) and (max-width:1500px)": {
                    p: "28px 18px",
                  },
                }}
              >
                {/* Icon Container */}
                <Box sx={{ mb: { xs: 1.5, lg: 2 }, display: "flex" }}>
                  <Box
                    component="img"
                    src={`${ASSET_BASE}/assets/${card.icon}`}
                    alt=""
                    sx={{
                      width: { xs: 58, lg: 64 },
                      height: { xs: 58, lg: 64 },
                      "@media (min-width:1200px) and (max-width:1500px)": {
                        width: 60,
                        height: 60,
                      },
                    }}
                  />
                </Box>

                {/* Heading */}
                <Typography
                  variant="h4"
                  sx={{
                    mb: 1.5,
                    fontWeight: 700,
                    whiteSpace: "pre-line",
                    letterSpacing: "0.6px",
                    lineHeight: 1.1,
                    fontSize: {
                      xs: "2rem",
                      lg: "2.6rem", // Perfect for Mac
                    },
                    /* Overrides for Windows Laptop */
                    "@media (min-width:1200px) and (max-width:1500px)": {
                      fontSize: "2.3rem",
                    },
                  }}
                >
                  {card.title}
                </Typography>

                {/* Body Text */}
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.96,
                    letterSpacing: "0.01em",
                    fontSize: {
                      xs: "1rem",
                      lg: "1.125rem", // Perfect for Mac
                    },
                    lineHeight: {
                      xs: 1.3,
                      lg: 1.5, // Perfect for Mac
                    },

                    "@media (min-width:1024px) and (max-width:1550px)": {
                      fontSize: "0.92rem !important",

                      lineHeight: "1.3 !important",

                      marginBottom: "20px !important",
                    },
                  }}
                >
                  {card.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default Careers;
